import React, { useState } from 'react';
import { Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { YouTubeEmbed } from './YouTubeEmbed';
import { UserProfileEditDialog } from './UserProfileEditDialog';
import { UserProfile } from '../../services/userProfileService';

interface LeaderboardRowExpandedProps {
  profile: UserProfile;
  onProfileUpdate?: (updated: UserProfile) => void;
}

interface StatCardProps {
  label: string;
  value: string | number;
}

const StatCard: React.FC<StatCardProps> = ({ label, value }) => (
  <div className="flex flex-col p-3 bg-muted/50 rounded-md border">
    <span className="text-xs text-muted-foreground mb-1">{label}</span>
    <span className="text-lg font-semibold">{value}</span>
  </div>
);

export const LeaderboardRowExpanded: React.FC<LeaderboardRowExpandedProps> = ({
  profile,
  onProfileUpdate
}) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const displayName = profile.show_email
    ? profile.email
    : (profile.display_name || profile.email);

  return (
    <div className="p-4 space-y-3 bg-card/50 w-full max-w-full">
      {/* Profile Header - Desktop */}
      <div className="hidden lg:flex items-start justify-between gap-3 w-full max-w-full">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold break-words overflow-hidden">
            {displayName}
          </h3>
          {profile.bio && (
            <p className="text-xs text-muted-foreground break-words whitespace-pre-wrap line-clamp-2 mt-1">
              {profile.bio}
            </p>
          )}
        </div>
        {profile.is_current_user && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditDialogOpen(true)}
            className="flex-shrink-0"
          >
            <Edit className="h-4 w-4 mr-1" />
            Edit Profile
          </Button>
        )}
      </div>

      {/* Profile Header - Mobile */}
      <div className="lg:hidden space-y-3">
        {/* Email and Edit Button Row */}
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-medium break-words overflow-hidden flex-1 min-w-0">
            {displayName}
          </h3>
          {profile.is_current_user && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditDialogOpen(true)}
              className="flex-shrink-0"
            >
              <Edit className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Bio */}
        {profile.bio && (
          <p className="text-xs text-muted-foreground break-words whitespace-pre-wrap">
            {profile.bio}
          </p>
        )}
      </div>

      {/* Two Column Layout: Stats Left, Video Right - Desktop */}
      <div className="hidden lg:grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column: Stats */}
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Statistics</h4>
          <div className="grid grid-cols-2 gap-4">
            <StatCard
              label="Total Uploads"
              value={profile.stats.total_uploads}
            />
            <StatCard
              label="This Month"
              value={profile.stats.uploads_this_month}
            />
            <StatCard
              label="Member Since"
              value={formatDate(profile.stats.member_since)}
            />
            <StatCard
              label="Monthly Avg"
              value={profile.stats.uploads_per_month_avg.toFixed(1)}
            />
          </div>
        </div>

        {/* Right Column: Featured Video */}
        {profile.featured_tune && (
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Featured Video
            </h4>
            <div className="max-w-md">
              <YouTubeEmbed
                youtubeId={profile.featured_tune.youtube_id}
                title={profile.featured_tune.video_title}
              />
            </div>
          </div>
        )}
      </div>

      {/* Mobile Layout: Video then Stats */}
      <div className="lg:hidden space-y-4">
        {/* Featured Video */}
        {profile.featured_tune && (
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-muted-foreground">
              Featured Video
            </h4>
            <YouTubeEmbed
              youtubeId={profile.featured_tune.youtube_id}
              title={profile.featured_tune.video_title}
            />
          </div>
        )}

        {/* Statistics */}
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-muted-foreground">Statistics</h4>
          <div className="grid grid-cols-2 gap-2">
            <StatCard
              label="Total Uploads"
              value={profile.stats.total_uploads}
            />
            <StatCard
              label="This Month"
              value={profile.stats.uploads_this_month}
            />
            <StatCard
              label="Member Since"
              value={formatDate(profile.stats.member_since)}
            />
            <StatCard
              label="Monthly Avg"
              value={profile.stats.uploads_per_month_avg.toFixed(1)}
            />
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      {profile.is_current_user && (
        <UserProfileEditDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          profile={profile}
          onSave={onProfileUpdate}
        />
      )}
    </div>
  );
};
